import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { api } from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../components/ui/use-toast';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';

const AdminProducts = () => {
  const navigate = useNavigate();
  const { isAdmin, adminToken } = useAdmin();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    image: '',
    images: [],
    collection_id: '',
    description: '',
    ingredients: '',
    nutrition: '',
    customizable: false,
    in_stock: true
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [productsData, collectionsData] = await Promise.all([
        api.getProducts(),
        api.getCollections()
      ]);
      setProducts(productsData);
      setCollections(collectionsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingProduct) {
        await api.adminUpdateProduct(editingProduct._id, productData, adminToken);
        toast({ title: 'Product updated successfully!' });
      } else {
        await api.adminCreateProduct(productData, adminToken);
        toast({ title: 'Product created successfully!' });
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast({
        title: 'Error',
        description: 'Failed to save product',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      image: product.image,
      images: product.images || [],
      collection_id: product.collection_id,
      description: product.description,
      ingredients: product.ingredients || '',
      nutrition: product.nutrition || '',
      customizable: product.customizable,
      in_stock: product.in_stock
    });
    setDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await api.adminDeleteProduct(productId, adminToken);
      toast({ title: 'Product deleted successfully!' });
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      price: '',
      image: '',
      images: [],
      collection_id: '',
      description: '',
      ingredients: '',
      nutrition: '',
      customizable: false,
      in_stock: true
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button onClick={() => navigate('/admin')} className="mr-4">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Manage Products</h1>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Product Name</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleChange} required />
                  </div>
                  <div>
                    <Label htmlFor="price">Price</Label>
                    <Input id="price" name="price" type="number" step="0.01" value={formData.price} onChange={handleChange} required />
                  </div>
                  <div>
                    <Label>Main Product Image</Label>
                    <ImageUpload
                      value={formData.image ? [formData.image] : []}
                      onChange={(images) => setFormData({ ...formData, image: images[0] || '' })}
                      multiple={false}
                    />
                  </div>
                  <div>
                    <Label>Additional Images (for carousel)</Label>
                    <ImageUpload
                      value={formData.images}
                      onChange={(images) => setFormData({ ...formData, images })}
                      multiple={true}
                      maxFiles={20}
                    />
                    <p className="text-xs text-gray-500 mt-1">You can upload up to 20 additional images</p>
                  </div>
                  <div>
                    <Label htmlFor="collection_id">Collection</Label>
                    <Select name="collection_id" value={formData.collection_id} onValueChange={(value) => setFormData({...formData, collection_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select collection" />
                      </SelectTrigger>
                      <SelectContent>
                        {collections.map(col => (
                          <SelectItem key={col._id} value={col._id}>{col.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea id="description" name="description" value={formData.description} onChange={handleChange} rows={3} required />
                  </div>
                  <div>
                    <Label htmlFor="ingredients">Ingredients</Label>
                    <Textarea 
                      id="ingredients" 
                      name="ingredients" 
                      value={formData.ingredients} 
                      onChange={handleChange} 
                      rows={3} 
                      placeholder="e.g., Dark chocolate (cocoa mass, sugar, cocoa butter), milk powder, vanilla..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="nutrition">Nutrition Information</Label>
                    <Textarea 
                      id="nutrition" 
                      name="nutrition" 
                      value={formData.nutrition} 
                      onChange={handleChange} 
                      rows={4} 
                      placeholder="Per serving: Calories 150, Fat 8g, Carbs 18g, Protein 2g..."
                    />
                  </div>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input type="checkbox" name="customizable" checked={formData.customizable} onChange={handleChange} className="mr-2" />
                      Customizable
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" name="in_stock" checked={formData.in_stock} onChange={handleChange} className="mr-2" />
                      In Stock
                    </label>
                  </div>
                  <Button type="submit" className="w-full">
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <Card key={product._id} className="p-6">
              <img src={product.image} alt={product.name} className="w-full h-48 object-cover rounded mb-4" />
              <h3 className="font-medium text-gray-900 mb-2">{product.name}</h3>
              <p className="text-gray-600 text-sm mb-2">${product.price.toFixed(2)}</p>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(product)}>
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(product._id)}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
